import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Bot,
  Check,
  ChevronRight,
  Clock3,
  Database,
  Pause,
  Play,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Waypoints,
  X,
} from 'lucide-react';
import {
  getRemiRun,
  getRemiWakeState,
  listRemiInstances,
  listRemiRuns,
  RemiActivityRecord,
  RemiRunRecord,
  setRemiInstanceStatus,
} from '../api/remiControl';
import { useOrganizationStore } from '../stores/useOrganizationStore';

type InspectorMode = 'authorization' | 'bundle' | 'commit';

const formatTime = (value?: string | null, includeDate = false): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(undefined, {
    month: includeDate ? 'short' : undefined,
    day: includeDate ? 'numeric' : undefined,
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  }).format(date);
};

const readable = (value?: string | null): string =>
  String(value || 'unknown')
    .replaceAll('_', ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const PHASES = [
  'Authenticate',
  'Context',
  'Plan',
  'Reason',
  'Policy',
  'Execute',
  'Verify',
  'Commit',
] as const;

const completedPhaseIndex = (phase: string, status: string): number => {
  if (status === 'completed') return PHASES.length - 1;
  const normalized = phase.toLowerCase();
  if (normalized.includes('commit')) return 7;
  if (normalized.includes('verif')) return 6;
  if (normalized.includes('execut') || normalized.includes('tool')) return 5;
  if (normalized.includes('policy') || normalized.includes('confirm')) return 4;
  if (normalized.includes('reason')) return 3;
  if (normalized.includes('plan')) return 2;
  if (normalized.includes('context')) return 1;
  if (normalized.includes('auth')) return 0;
  return -1;
};

const statusTone = (status?: string): string => {
  if (['completed', 'delivered', 'accepted', 'active', 'agentic'].includes(status || '')) {
    return 'bg-emerald-500';
  }
  if (['failed', 'dead_letter', 'disabled', 'rejected'].includes(status || '')) {
    return 'bg-rose-500';
  }
  if (['pending', 'committing', 'running', 'leased', 'interrupted'].includes(status || '')) {
    return 'bg-amber-500';
  }
  return 'bg-slate-400';
};

const StatusDot: React.FC<{ status?: string }> = ({ status }) => (
  <span className={`inline-block h-2 w-2 rounded-full ${statusTone(status)}`} aria-hidden />
);

const EmptyState: React.FC<{ title: string; detail: string }> = ({ title, detail }) => (
  <div className="flex min-h-52 flex-col items-center justify-center px-8 text-center">
    <Waypoints className="mb-4 h-8 w-8 text-slate-300" aria-hidden />
    <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{title}</p>
    <p className="mt-1 max-w-sm text-sm text-slate-500 dark:text-slate-400">{detail}</p>
  </div>
);

const Metric: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="min-w-0 border-r border-slate-200 px-4 last:border-r-0 dark:border-slate-700">
    <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-500">{label}</p>
    <div className="mt-1 truncate text-sm font-semibold text-slate-900 dark:text-white">
      {value}
    </div>
  </div>
);

const ActivityRow: React.FC<{ activity: RemiActivityRecord }> = ({ activity }) => (
  <li className="relative grid grid-cols-[28px_minmax(0,1fr)_auto] gap-3 pb-6 last:pb-0">
    <div className="relative flex justify-center">
      <span className="absolute bottom-[-24px] top-5 w-px bg-slate-200 last:hidden dark:bg-slate-700" />
      <span className="relative mt-1 flex h-5 w-5 items-center justify-center rounded-full border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
        <StatusDot status={activity.status} />
      </span>
    </div>
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-2">
        <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">
          {readable(activity.activity_name)}
        </p>
        <span className="text-[11px] font-medium uppercase tracking-wider text-slate-400">
          {activity.activity_type}
        </span>
      </div>
      <p className="mt-1 font-mono text-[11px] text-slate-500">
        {activity.evidence_reference || 'No evidence reference'}
      </p>
      {activity.failure_detail && (
        <p className="mt-2 text-xs text-rose-600 dark:text-rose-400">{activity.failure_detail}</p>
      )}
    </div>
    <div className="text-right text-[11px] text-slate-500">
      <p>{formatTime(activity.completed_at || activity.started_at)}</p>
      {activity.external_effect && <p className="mt-1 text-amber-700">remote effect</p>}
    </div>
  </li>
);

const JsonRows: React.FC<{ value: Record<string, unknown> | null | undefined }> = ({ value }) => {
  if (!value || Object.keys(value).length === 0) {
    return <p className="text-sm text-slate-500">No recorded data.</p>;
  }
  return (
    <dl className="divide-y divide-slate-100 dark:divide-slate-800">
      {Object.entries(value).map(([key, raw]) => (
        <div
          key={key}
          className="grid grid-cols-[minmax(110px,0.8fr)_minmax(0,1.2fr)] gap-3 py-2.5"
        >
          <dt className="break-words text-xs font-medium text-slate-500">{readable(key)}</dt>
          <dd className="break-words text-right font-mono text-[11px] leading-5 text-slate-800 dark:text-slate-200">
            {typeof raw === 'string' || typeof raw === 'number' || typeof raw === 'boolean'
              ? String(raw)
              : JSON.stringify(raw)}
          </dd>
        </div>
      ))}
    </dl>
  );
};

const RunItem: React.FC<{
  run: RemiRunRecord;
  selected: boolean;
  onSelect: () => void;
}> = ({ run, selected, onSelect }) => (
  <button
    type="button"
    onClick={onSelect}
    aria-current={selected ? 'true' : undefined}
    className={`group w-full border-b border-slate-100 px-4 py-3.5 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-inset dark:border-slate-800 ${
      selected ? 'bg-amber-50/80 dark:bg-amber-950/20' : 'hover:bg-slate-50 dark:hover:bg-slate-900'
    }`}
  >
    <div className="flex items-start gap-3">
      <StatusDot status={run.status} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">
            {readable(run.relationship_lane)}
          </p>
          <span className="shrink-0 text-[11px] text-slate-500">{formatTime(run.created_at)}</span>
        </div>
        <p className="mt-1 truncate text-xs text-slate-500">
          {readable(run.trigger_type)} · Tier {run.role_tier} · {readable(run.current_phase)}
        </p>
        <p className="mt-2 truncate font-mono text-[10px] text-slate-400">{run.run_id}</p>
      </div>
      <ChevronRight
        className={`mt-4 h-4 w-4 shrink-0 ${selected ? 'text-amber-600' : 'text-slate-300'}`}
      />
    </div>
  </button>
);

export function RemiControl(): React.ReactElement {
  const queryClient = useQueryClient();
  const organization = useOrganizationStore((state) => state.organization);
  const organizationId = organization?.id || '';
  const [instanceId, setInstanceId] = useState('');
  const [runId, setRunId] = useState('');
  const [inspectorMode, setInspectorMode] = useState<InspectorMode>('authorization');
  const [statusDialog, setStatusDialog] = useState<'pause' | 'resume' | null>(null);
  const [reason, setReason] = useState('');

  const instancesQuery = useQuery({
    queryKey: ['remi-control', 'instances', organizationId],
    queryFn: () => listRemiInstances(organizationId),
    enabled: Boolean(organizationId),
    refetchInterval: 30_000,
  });
  const instances = instancesQuery.data || [];

  useEffect(() => {
    if (!instances.length) {
      setInstanceId('');
      return;
    }
    if (!instances.some((instance) => instance.instance_id === instanceId)) {
      setInstanceId(instances[0].instance_id);
    }
  }, [instanceId, instances]);

  const selectedInstance = instances.find((instance) => instance.instance_id === instanceId);
  const runsQuery = useQuery({
    queryKey: ['remi-control', 'runs', organizationId, instanceId],
    queryFn: () => listRemiRuns(organizationId, instanceId),
    enabled: Boolean(organizationId && instanceId),
    refetchInterval: 15_000,
  });
  const runs = runsQuery.data || [];

  useEffect(() => {
    if (!runs.length) {
      setRunId('');
      return;
    }
    if (!runs.some((run) => run.run_id === runId)) setRunId(runs[0].run_id);
  }, [runId, runs]);

  const runQuery = useQuery({
    queryKey: ['remi-control', 'run', organizationId, instanceId, runId],
    queryFn: () => getRemiRun(organizationId, instanceId, runId),
    enabled: Boolean(organizationId && instanceId && runId),
    refetchInterval: (query) =>
      ['completed', 'failed', 'canceled'].includes(query.state.data?.status || '') ? false : 5_000,
  });
  const wakeQuery = useQuery({
    queryKey: ['remi-control', 'wake', organizationId, instanceId],
    queryFn: () => getRemiWakeState(organizationId, instanceId),
    enabled: Boolean(organizationId && instanceId),
    refetchInterval: 15_000,
  });

  const statusMutation = useMutation({
    mutationFn: (action: 'pause' | 'resume') =>
      setRemiInstanceStatus(organizationId, instanceId, action, reason.trim()),
    onSuccess: async () => {
      setStatusDialog(null);
      setReason('');
      await queryClient.invalidateQueries({ queryKey: ['remi-control'] });
    },
  });

  const run = runQuery.data;
  const unresolvedDelivery = useMemo(
    () => run?.outbox.filter((item) => !['delivered'].includes(item.status)).length || 0,
    [run]
  );
  const pendingConfirmation = run?.confirmations.find((item) => item.status === 'pending');
  const runPhaseIndex = run ? completedPhaseIndex(run.current_phase, run.status) : -1;

  useEffect(() => {
    if (!statusDialog) return undefined;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setStatusDialog(null);
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [statusDialog]);

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['remi-control'] });
  };

  if (!organizationId) {
    return (
      <EmptyState
        title="Select an organization"
        detail="The control plane requires an explicit organization scope."
      />
    );
  }

  return (
    <main className="min-h-[calc(100vh-90px)] bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-white">
      <div className="border-b border-slate-200 bg-white px-4 py-4 dark:border-slate-800 dark:bg-slate-950 md:px-6 3xl:px-14">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-amber-600" aria-hidden />
              <h1 className="text-lg font-semibold tracking-tight">Remi Agent Fabric</h1>
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-800">
                Dev
              </span>
            </div>
            <p className="mt-1 text-sm text-slate-500">
              Run lineage, authorization, evidence, delivery, and recovery.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="sr-only" htmlFor="remi-instance">
              Remi instance
            </label>
            <select
              id="remi-instance"
              value={instanceId}
              onChange={(event) => setInstanceId(event.target.value)}
              className="h-9 min-w-64 rounded-md border-slate-300 bg-white text-sm shadow-none focus:border-amber-500 focus:ring-amber-500 dark:border-slate-700 dark:bg-slate-900"
            >
              {instances.map((instance) => (
                <option key={instance.instance_id} value={instance.instance_id}>
                  {instance.name} · {readable(instance.instance_type)}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={refresh}
              className="inline-flex h-9 items-center gap-2 rounded-md border border-slate-300 bg-white px-3 text-sm font-medium hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 dark:border-slate-700 dark:bg-slate-900 dark:hover:bg-slate-800"
            >
              <RefreshCw className={`h-4 w-4 ${instancesQuery.isFetching ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            {selectedInstance && (
              <button
                type="button"
                onClick={() =>
                  setStatusDialog(selectedInstance.status === 'active' ? 'pause' : 'resume')
                }
                className={`inline-flex h-9 items-center gap-2 rounded-md px-3 text-sm font-semibold text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                  selectedInstance.status === 'active'
                    ? 'bg-slate-800 hover:bg-slate-700 focus-visible:ring-slate-700'
                    : 'bg-amber-600 hover:bg-amber-500 focus-visible:ring-amber-600'
                }`}
              >
                {selectedInstance.status === 'active' ? (
                  <Pause className="h-4 w-4" />
                ) : (
                  <Play className="h-4 w-4" />
                )}
                {selectedInstance.status === 'active' ? 'Pause' : 'Resume'}
              </button>
            )}
          </div>
        </div>
      </div>

      {instancesQuery.isError ? (
        <div className="m-6 border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
          Agent Fabric state could not be loaded. Verify the API migration and your organization
          scope.
        </div>
      ) : !selectedInstance ? (
        <EmptyState
          title="No Remi instances"
          detail="This organization has no provisioned Agent Fabric identities."
        />
      ) : (
        <>
          <section
            aria-label="Instance status"
            className="grid grid-cols-2 divide-y divide-slate-200 border-b border-slate-200 bg-white py-3 dark:divide-slate-800 dark:border-slate-800 dark:bg-slate-950 md:grid-cols-4 md:divide-y-0 xl:grid-cols-7 3xl:px-10"
          >
            <Metric
              label="Instance"
              value={
                <span className="inline-flex items-center gap-2">
                  <StatusDot status={selectedInstance.status} />
                  {readable(selectedInstance.status)}
                </span>
              }
            />
            <Metric label="Runtime" value={readable(selectedInstance.runtime_mode)} />
            <Metric label="Config" value={`v${selectedInstance.configuration_version}`} />
            <Metric
              label="Wake"
              value={
                selectedInstance.wake_enabled
                  ? readable(wakeQuery.data?.status || 'enabled')
                  : 'Disabled'
              }
            />
            <Metric
              label="Learning"
              value={selectedInstance.learning_enabled ? 'Capture on' : 'Disabled'}
            />
            <Metric label="Confirmation" value={pendingConfirmation ? 'Waiting' : 'Clear'} />
            <Metric label="Delivery issues" value={unresolvedDelivery || 'None'} />
          </section>

          <div className="grid min-h-[680px] grid-cols-1 xl:grid-cols-[300px_minmax(460px,1fr)_380px]">
            <aside
              className="border-r border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950"
              aria-label="Recent runs"
            >
              <div className="flex h-12 items-center justify-between border-b border-slate-200 px-4 dark:border-slate-800">
                <h2 className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
                  Recent runs
                </h2>
                <span className="text-xs text-slate-400">{runs.length}</span>
              </div>
              {runsQuery.isLoading ? (
                <div className="space-y-3 p-4" aria-label="Loading runs">
                  {[0, 1, 2, 3].map((item) => (
                    <div
                      key={item}
                      className="h-16 animate-pulse rounded bg-slate-100 dark:bg-slate-900"
                    />
                  ))}
                </div>
              ) : runs.length ? (
                <div className="max-h-[calc(100vh-250px)] overflow-y-auto">
                  {runs.map((item) => (
                    <RunItem
                      key={item.run_id}
                      run={item}
                      selected={item.run_id === runId}
                      onSelect={() => setRunId(item.run_id)}
                    />
                  ))}
                </div>
              ) : (
                <EmptyState
                  title="No runs yet"
                  detail="Runs appear after this instance receives a turn or wake."
                />
              )}
            </aside>

            <section className="min-w-0 bg-slate-50 dark:bg-slate-950" aria-label="Run timeline">
              {!run ? (
                <EmptyState
                  title={runQuery.isLoading ? 'Loading run' : 'Select a run'}
                  detail="Choose a run to inspect its phases and evidence."
                />
              ) : (
                <>
                  <header className="border-b border-slate-200 bg-white px-5 py-4 dark:border-slate-800 dark:bg-slate-950">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <StatusDot status={run.status} />
                          <h2 className="text-base font-semibold">
                            {readable(run.relationship_lane)} run
                          </h2>
                          <span className="text-xs text-slate-500">Tier {run.role_tier}</span>
                        </div>
                        <p className="mt-1 font-mono text-[11px] text-slate-400">{run.run_id}</p>
                      </div>
                      <div className="text-right text-xs text-slate-500">
                        <p>
                          {readable(run.trigger_type)} · {readable(run.execution_mode)}
                        </p>
                        <p className="mt-1">
                          Started {formatTime(run.started_at || run.created_at, true)}
                        </p>
                      </div>
                    </div>
                    <div
                      className="mt-4 flex items-center gap-1 overflow-x-auto"
                      aria-label="Run phases"
                    >
                      {PHASES.map((phase, index) => (
                        <React.Fragment key={phase}>
                          <span
                            className={`whitespace-nowrap rounded px-2 py-1 text-[10px] font-semibold ${index <= runPhaseIndex ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900' : 'bg-slate-200 text-slate-500 dark:bg-slate-800'}`}
                          >
                            {phase}
                          </span>
                          {index < 7 && (
                            <span className="h-px min-w-2 flex-1 bg-slate-300 dark:bg-slate-700" />
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  </header>

                  <div className="p-5 md:p-6">
                    {run.failure_detail && (
                      <div className="mb-6 flex gap-3 border-l-2 border-rose-500 bg-rose-50 p-3 text-sm text-rose-800 dark:bg-rose-950/20 dark:text-rose-300">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                        <div>
                          <p className="font-semibold">{readable(run.failure_class)}</p>
                          <p className="mt-1">{run.failure_detail}</p>
                        </div>
                      </div>
                    )}

                    {run.confirmations.map((confirmation) => (
                      <div
                        key={confirmation.confirmation_id}
                        className="mb-6 border-l-2 border-amber-500 bg-amber-50 p-4 dark:bg-amber-950/20"
                      >
                        <div className="flex items-center gap-2 text-sm font-semibold text-amber-900 dark:text-amber-200">
                          <ShieldCheck className="h-4 w-4" /> Confirmation{' '}
                          {readable(confirmation.status)}
                        </div>
                        <p className="mt-2 text-sm text-amber-900/80 dark:text-amber-200/80">
                          {readable(confirmation.action_name)}
                        </p>
                        <p className="mt-1 truncate font-mono text-[10px] text-amber-700">
                          {confirmation.action_digest}
                        </p>
                      </div>
                    ))}

                    <div className="mb-4 flex items-center justify-between">
                      <h3 className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
                        Activity evidence
                      </h3>
                      <span className="text-xs text-slate-400">
                        {run.activities.length} records
                      </span>
                    </div>
                    {run.activities.length ? (
                      <ol>
                        {run.activities.map((activity) => (
                          <ActivityRow key={activity.activity_id} activity={activity} />
                        ))}
                      </ol>
                    ) : (
                      <EmptyState
                        title="No activity evidence"
                        detail="This run has not recorded a model, tool, action, or verifier activity."
                      />
                    )}

                    {run.outbox.length > 0 && (
                      <div className="mt-8 border-t border-slate-200 pt-6 dark:border-slate-800">
                        <h3 className="mb-3 text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
                          Remote delivery
                        </h3>
                        <div className="divide-y divide-slate-200 border-y border-slate-200 dark:divide-slate-800 dark:border-slate-800">
                          {run.outbox.map((item) => (
                            <div
                              key={item.outbox_id}
                              className="grid grid-cols-[1fr_auto] gap-4 py-3 text-sm"
                            >
                              <div>
                                <p className="font-medium">{readable(item.operation)}</p>
                                <p className="mt-1 text-xs text-slate-500">
                                  {item.destination} · attempt {item.attempt_count}
                                </p>
                              </div>
                              <div className="flex items-center gap-2 text-xs">
                                <StatusDot status={item.status} />
                                {readable(item.status)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </section>

            <aside
              className="border-l border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950"
              aria-label="Run inspector"
            >
              <div className="sticky top-0">
                <div
                  className="flex h-12 items-center border-b border-slate-200 px-2 dark:border-slate-800"
                  role="tablist"
                  aria-label="Inspector sections"
                >
                  {(['authorization', 'bundle', 'commit'] as InspectorMode[]).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      role="tab"
                      aria-selected={inspectorMode === mode}
                      onClick={() => setInspectorMode(mode)}
                      className={`h-full border-b-2 px-3 text-xs font-semibold capitalize focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 ${inspectorMode === mode ? 'border-amber-500 text-slate-900 dark:text-white' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
                    >
                      {mode}
                    </button>
                  ))}
                </div>
                <div className="max-h-[calc(100vh-250px)] overflow-y-auto p-4">
                  {!run ? (
                    <p className="text-sm text-slate-500">
                      Select a run to inspect its immutable controls.
                    </p>
                  ) : (
                    <>
                      <div className="mb-5 flex items-center gap-2 text-xs text-slate-500">
                        {inspectorMode === 'authorization' ? (
                          <ShieldCheck className="h-4 w-4" />
                        ) : inspectorMode === 'bundle' ? (
                          <Sparkles className="h-4 w-4" />
                        ) : (
                          <Database className="h-4 w-4" />
                        )}
                        {inspectorMode === 'authorization'
                          ? 'Execution-time authority'
                          : inspectorMode === 'bundle'
                            ? 'Pinned release inputs'
                            : 'Durable result truth'}
                      </div>
                      <JsonRows
                        value={
                          inspectorMode === 'authorization'
                            ? run.authorization_envelope
                            : inspectorMode === 'bundle'
                              ? run.pinned_bundle
                              : run.commit || undefined
                        }
                      />
                    </>
                  )}
                </div>
              </div>
            </aside>
          </div>
        </>
      )}

      {statusDialog && selectedInstance && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/50 p-4"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setStatusDialog(null);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="instance-status-title"
            className="w-full max-w-md rounded-lg bg-white p-5 shadow-2xl dark:bg-slate-900"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 id="instance-status-title" className="text-base font-semibold">
                  {statusDialog === 'pause' ? 'Pause' : 'Resume'} {selectedInstance.name}
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  This appends an immutable operator event. Pausing cancels active wake leases and
                  never invokes legacy.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setStatusDialog(null)}
                aria-label="Close"
                className="rounded p-1 text-slate-500 hover:bg-slate-100"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <label
              htmlFor="instance-status-reason"
              className="mt-5 block text-xs font-semibold uppercase tracking-wider text-slate-500"
            >
              Reason
            </label>
            <textarea
              id="instance-status-reason"
              autoFocus
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              rows={3}
              className="mt-2 w-full rounded-md border-slate-300 text-sm focus:border-amber-500 focus:ring-amber-500 dark:border-slate-700 dark:bg-slate-950"
              placeholder="What changed, and why?"
            />
            {statusMutation.isError && (
              <p className="mt-2 text-sm text-rose-600">
                The status change failed. Refresh state and try again.
              </p>
            )}
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setStatusDialog(null)}
                className="h-9 rounded-md px-3 text-sm font-medium text-slate-600 hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={reason.trim().length < 3 || statusMutation.isPending}
                onClick={() => statusMutation.mutate(statusDialog)}
                className="inline-flex h-9 items-center gap-2 rounded-md bg-amber-600 px-3 text-sm font-semibold text-white hover:bg-amber-500 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {statusMutation.isPending ? (
                  <Clock3 className="h-4 w-4 animate-spin" />
                ) : statusDialog === 'pause' ? (
                  <Pause className="h-4 w-4" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
                {statusDialog === 'pause' ? 'Pause instance' : 'Resume instance'}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
